import React, { useState, useEffect, useMemo } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer, View, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay, addDays } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar.css';
import AppointmentService from '../../services/appointmentService';
import { ToastType, useToast } from '../../pages/toast/ToastProvider';
import { CalendarEvent } from '../../types/CalendarEvent';
import AppointmentEditorModal from '../../modals/AppointmentEditorModal';
import type { Appointment } from '../../types/Appointment';
import AppointmentHelpers from '../../utils/appointmentHelper';
import AppointmentCreateModal from '../../modals/AppointmentCreateModal';
import DateTimeHelper from '../../utils/dateTimeHelper';
import { useTranslation } from 'react-i18next';
import { AppointmentConfiguration, AppointmentMode } from '../../types/AppointmentConfiguration';
import AppointmentConfigurationService from '../../services/appointmentConfigurationService';
import { useNavigate } from 'react-router-dom';
import { RefreshCcw } from 'lucide-react';
import { he } from 'date-fns/locale';

const locales = {
    'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
    format,
    parse: (dateString: string, format: string) => parse(dateString, format, new Date()),
    startOfWeek: (date: any) => startOfWeek(date, { weekStartsOn: 0 }), // Set Sunday as the first day of the week
    getDay: (date: Date) => date.getDay(),
    locales,
});

const Calendar = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'he';    
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [view, setView] = useState<View>('month');
    
    // State to track current display range
    const [displayRange, setDisplayRange] = useState<{start: Date, end: Date} | null>(null);    
    const [appointmentConfig, setAppointmentConfig] = useState<AppointmentConfiguration | null>(null);
    const { showToast } = useToast();
    const navigate = useNavigate();

        // Calendar English - hebrew format
    const dayNames = useMemo(() => ({
        en: {
            short: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            long: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        },
        he: {
            short: ['יום א', 'יום ב', 'יום ג', 'יום ד', 'יום ה', 'יום ו', 'שבת'],
            long: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']
        }
    }), []);

    const monthNames = useMemo(() => ({
        en: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        he: ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
            'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']
    }), []);

    const calendarFormats = useMemo(() => ({
        dayFormat: (date: Date, culture: any, localizer: any) => {
            const dayIndex = date.getDay();
            return isRtl ? dayNames.he.short[dayIndex] : dayNames.en.short[dayIndex];
        },

        weekdayFormat: (date: Date, culture: any, localizer: any) => {
            const dayIndex = date.getDay();
            return isRtl ? dayNames.he.short[dayIndex] : dayNames.en.short[dayIndex];
        },

        monthHeaderFormat: (date: Date, culture: any, localizer: any) => {
            const month = date.getMonth();
            const year = date.getFullYear();
            const monthName = isRtl ? monthNames.he[month] : monthNames.en[month];
            return `${monthName} ${year}`;
        },

        timeGutterFormat: "HH:mm",


        // ... other formats
    }), [isRtl, dayNames, monthNames]);

    
    // Function to calculate display range based on current view and date
    const calculateDisplayRange = (date: Date, currentView: View) => {
        let start: Date;
        let end: Date;

        switch (currentView) {
            case Views.MONTH:
                start = startOfWeek(startOfMonth(date), { weekStartsOn: 0 }); // Start of first week of month
                end = endOfMonth(date);
                // For month view, extend to end of last week shown
                const daysToAdd = 6 - end.getDay();
                end = addDays(end, daysToAdd);
                break;
            
            case Views.WEEK:
                start = startOfWeek(date, { weekStartsOn: 0 }); // Start of week (Sunday)
                end = addDays(start, 6); // End of week (Saturday)
                break;
            
            case Views.DAY:
                start = startOfDay(date);
                end = endOfDay(date);
                break;
            
            default:
                start = startOfWeek(startOfMonth(date), { weekStartsOn: 0 });
                end = endOfMonth(date);
        }

        return { start, end };
    };

    // Update display range whenever date or view changes
    useEffect(() => {
        const range = calculateDisplayRange(currentDate, view);
        setDisplayRange(range);        
    }, [currentDate, view]);

    // Initialize display range on component mount
    useEffect(() => {
        const initialRange = calculateDisplayRange(currentDate, view);
        setDisplayRange(initialRange);
    }, []); // Empty dependency array for initial load only

    useEffect(() => {

        const controller = new AbortController();
        const clinicId = sessionStorage.getItem('activeClinicId');

        const fetchAppointmentConfig = async () => {
            if (clinicId) {
                try {
                    let appointmentConfig = await AppointmentConfigurationService.getAppointmentConfiguration(clinicId, controller.signal, navigate);
                    setAppointmentConfig(appointmentConfig);

                } catch (error) {
                    console.error('Failed to fetch appointment config', error);
                }
            }
        };

        fetchAppointmentConfig();

        // return () => {
        //     controller.abort();
        // };

    }, []); // execute just on init

    const fetchEvents = async () => {
        try {
            const controller = new AbortController();
            const clinicId = sessionStorage.getItem('activeClinicId');
            if (!clinicId || !displayRange) { return };
            //console.log(`START = ${displayRange.start}, END = ${displayRange.end}`);
            const appointments = await AppointmentService.getAppointment(clinicId, displayRange.start, displayRange.end, controller.signal);

            const fetchedEvents: CalendarEvent[] = appointments.map((q: any) => {
                const [hours, minutes] = q.time.split(':').map(Number);
                const start = new Date(q.date);
                start.setHours(hours, minutes, 0, 0);

                const end = new Date(start.getTime() + q.durationMinutes * 60000);
                const eventTitle = `${q.name} ${q.appointmentType?.name ? ` - ${q.appointmentType.name}` : ''} (${q.phone})`;

                return {
                    appointmentId: q._id,
                    clinicId: q.clinicId,
                    title: eventTitle,
                    start,
                    end,
                    comments: q.comments,
                    date: q.date,
                    durationMinutes: q.durationMinutes,
                    email: q.email,
                    name: q.name,
                    phone: q.phone,
                    time: q.time,
                    appointmentType: q.appointmentType,
                };
            });

            setEvents(fetchedEvents);
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                console.error('Failed to fetch events:', error);
            }
        }
    };

    useEffect(() => {
        fetchEvents();
    }, [currentDate, displayRange]); // Added displayRange as dependency

    const handleRefreshCalendar = () => {
        fetchEvents();
    };

    const [openForAppointmentDays, setOpenForAppointmentDays] = useState<Date[]>([
        //new Date(2025, 2, 5), // March 5, 2025
        //new Date(2025, 2, 10), // March 10, 2025
    ]);

    /********************/
    /* Edit Appointment */
    /********************/
    const [selectedAppointment, setSelectedAppointment] = useState<CalendarEvent | null>(null);
    const [showAppointmentEditorModal, setShowAppointmentEditorModal] = useState(false);

    const handleSelectAppointment = (event: CalendarEvent) => {
        const startDate = new Date(event.start);

        const formattedDate = DateTimeHelper.getFormatDate(startDate);
        const formattedTime = DateTimeHelper.getFormatTime(event.start);

        const updatedEvent: CalendarEvent = {
            ...event,
            date: formattedDate,
            time: formattedTime,
        };

        setSelectedAppointment(updatedEvent);
        setShowAppointmentEditorModal(true);
    };

    const handleUpdateAppointment = async (updatedEvent: CalendarEvent) => {
        setShowAppointmentEditorModal(false);
        const controller = new AbortController();
        const appointmentToUpdate: Appointment = AppointmentHelpers.convertCalendarEventToAppointment(updatedEvent);

        try {
            if (!appointmentToUpdate._id) {
                showToast(t(`toastMessages.CANT_UPDATE_APPOINTMENT`), ToastType.Warning);
                return;
            }

            const isEventUnchanged: boolean = AppointmentHelpers.isEventUnchanged(selectedAppointment, updatedEvent);

            if (isEventUnchanged) {
                showToast(t(`toastMessages.NO_CHANGES_APPOINTMENT`), ToastType.Success);
                return;
            }
             
            const updatedAppointment = await AppointmentService.updateAppointment(appointmentToUpdate._id,appointmentToUpdate, controller.signal);

            if (updatedAppointment?.error) {
                showToast(updatedAppointment.message, ToastType.Warning);
                return;
            }

            updateCalendarSlot(updatedEvent);
            showToast(t(`toastMessages.APPOINTMENT_BOOKED_SUCCESS`), ToastType.Success);
        } catch (error: any) {
            showToast(error.response?.data?.message || 'Failed to save appointment', ToastType.Error);
        }
    };

    const updateCalendarSlot = (updatedEvent: CalendarEvent) => {
        const updatedCalendarEvent = getRefreshedCalendarEvent(updatedEvent);

        if (updatedCalendarEvent) {
            setEvents(prevEvents =>
                prevEvents.map(event =>
                    event.appointmentId === updatedEvent.appointmentId ? updatedCalendarEvent : event
                )
            );
        }
    };

    const handleDeleteAppointment = async (appointmentId: string) => {
        setShowAppointmentEditorModal(false);

        if (!appointmentId) {
            showToast(t(`toastMessages.INVALID_APPOINTMENT_ID`), ToastType.Warning);
            return;
        }

        const controller = new AbortController();

        try {
            const response = await AppointmentService.deleteAppointment(appointmentId, controller.signal);

            // Optionally check for a standardized success structure
            if (!response || response.error) {
                showToast(response?.message || 'Failed to delete appointment.', ToastType.Warning);
                return;
            }

            // Remove the appointment from the calendar
            setEvents(prevEvents =>
                prevEvents.filter(event => event.appointmentId !== appointmentId)
            );

            showToast(t(`toastMessages.APPOINTMENT_DELETED_SUCCESS`), ToastType.Success);
        } catch (error: any) {
            console.error('Delete error:', error);
            showToast(error?.response?.data?.message || 'Failed to delete appointment.', ToastType.Error);
        }
    };


    /*******************************************************************************/
    /**********************/
    /* Create Appointment */
    /**********************/
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createModalDate, setCreateModalDate] = useState('');
    const [createModalTime, setCreateModalTime] = useState('');

    const handleSelectSlot = ({ start }: { start: Date }) => {
        setCreateModalDate(DateTimeHelper.getFormatDate(start));
        setCreateModalTime(DateTimeHelper.getFormatTime(start));
        setShowCreateModal(true);
    };

    const handleCreateAppointment = async (newEvent: CalendarEvent) => {
        const clinicId = sessionStorage.getItem('activeClinicId');
        const appointmentToCreate: Appointment = {
            ...AppointmentHelpers.convertCalendarEventToAppointment(newEvent),
            clinicId,
        };
        
        const language = i18n.language ?? "en";
        const isTypesMode =appointmentConfig?.appointmentMode === AppointmentMode.Types;
        const appointment: Appointment | null = await AppointmentHelpers.submitAppointment(appointmentToCreate, showToast, t, language, isTypesMode);

        if (appointment) {
            const newCalendarEvent = getRefreshedCalendarEvent(newEvent);

            if (newCalendarEvent) {
                newCalendarEvent.appointmentId = appointment._id;
                setEvents(prev => [...prev, newCalendarEvent,]);
            }

            setShowCreateModal(false);
        }
    }

    const getRefreshedCalendarEvent = (event: CalendarEvent): CalendarEvent | null => {
        if (!event.time || !event.date || !event.durationMinutes) {
            return null;
        }

        const [hours, minutes] = event.time.split(':').map(Number);
        const start = new Date(event.date);
        start.setHours(hours, minutes, 0, 0);

        const end = new Date(start.getTime() + event.durationMinutes * 60000);
        const eventTitle = `${event.name} ${event.appointmentType?.name ? ` - ${event.appointmentType.name}` : ''} (${event.phone})`;

        return {
            ...event,
            start,
            end,
            // title: `${event.name} (${event.phone})`,
            title: eventTitle,
        };
    };


    const handleDrillDown = (date: Date) => {
        setCurrentDate(date);
        setView('day');
    };

    const dayPropGetter = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isToday = date.toDateString() === today.toDateString();
        const isOpenForAppointment = openForAppointmentDays.some(
            (openDate) => openDate.toDateString() === date.toDateString()
        );

        let backgroundColor = "#191c24";

        if (isToday) {
             backgroundColor = "#8fb5c0";
        } else if (isOpenForAppointment) {
            backgroundColor = "#17a2b8";
        }

        return {
            style: {
                backgroundColor
            },
        };
    };

    const CustomToolbar = (props: any) => {
        const { label, onNavigate, onView, view } = props;
    
        return (
            <div className="rbc-toolbar">
                <span className="rbc-btn-group">
                    <button className="btn-today" onClick={() => onNavigate("TODAY")}>{t('today')}</button>
                    <button className="btn-prev" onClick={() => onNavigate("PREV")}>{t('prev')}</button>
                    <button className="btn-next" onClick={() => onNavigate("NEXT")}>{t('next')}</button>
                    {/* Button to show current range info */}
                    {/* <button className="btn-range" 
                        onClick={() => {
                            if (displayRange) {
                                alert(`Current Range:\nStart: ${format(displayRange.start, 'yyyy-MM-dd')}\nEnd: ${format(displayRange.end, 'yyyy-MM-dd')}\nView: ${view}`);
                            }
                        }}
                    >
                        Show Range Info
                    </button> */}

                    <button type="button" className={`btn-refresh ${isRtl ? 'me-3' : 'ms-3'}`} title={t('refresh-calendar')} onClick={handleRefreshCalendar}>
                          <RefreshCcw size={20} />
                    </button>

                </span>
    
                <span className="rbc-toolbar-label">
                    {label}
                    {displayRange && (
                        <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: '2px' }}>
                            {isRtl
                                ? `${format(displayRange.start, 'dd MMM', { locale: he })} - ${format(displayRange.end, 'dd MMM yyyy', { locale: he })}`
                                : `${format(displayRange.start, 'MMM dd')} - ${format(displayRange.end, 'MMM dd, yyyy')}`
                            }
                        </div>
                    )}
                </span>
    
                <span className="rbc-btn-group">
                    <button className={`btn-view ${view === Views.MONTH ? "active" : ""}`} onClick={() => onView(Views.MONTH)}>
                        {t('month')}
                    </button>
                    <button className={`btn-view ${view === Views.WEEK ? "active" : ""}`} onClick={() => onView(Views.WEEK)}>
                        {t('week')}
                    </button>
                    <button className={`btn-view ${view === Views.DAY ? "active" : ""}`} onClick={() => onView(Views.DAY)}>
                        {t('day')}
                    </button>
                </span>
            </div>
        );
    };

    return (
        <div>
            <div id="Calendar" style={{ flexGrow: 1 }}>
                <BigCalendar
                    events={events}
                    localizer={localizer}
                    startAccessor="start"
                    endAccessor="end"
                    selectable
                    defaultView="month"
                    views={["month", "week", "day"]}
                    date={currentDate}
                    view={view}
                    onView={setView}
                    onNavigate={setCurrentDate}
                    defaultDate={new Date()} // You can set this to a specific date
                    onSelectEvent={handleSelectAppointment}
                    onSelectSlot={handleSelectSlot}
                    onDrillDown={handleDrillDown}
                    dayPropGetter={dayPropGetter}
                    min={new Date(2023, 0, 1, 7, 0)}
                    components={{
                        toolbar: CustomToolbar,
                    }}
                    step={15}
                    timeslots={2}
                    formats={calendarFormats}
                    rtl={isRtl}
                    culture={isRtl ? 'he' : 'en'}
                    style={{
                        height: "80vh", flexGrow: 1,
                        direction: isRtl ? 'rtl' : 'ltr',
                        fontFamily: isRtl ? 'Arial, sans-serif' : 'inherit'
                    }}
                />
            </div>
           
            <AppointmentCreateModal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreate={handleCreateAppointment}
                defaultDate={createModalDate}
                defaultTime={createModalTime}
                appointmentTypes={appointmentConfig?.appointmentMode === AppointmentMode.Types ? appointmentConfig?.appointmentTypes ?? [] : null}
                durationMin={appointmentConfig?.appointmentMode === AppointmentMode.Types ? undefined : appointmentConfig?.duration}
            />

            <AppointmentEditorModal
                show={showAppointmentEditorModal}
                onClose={() => setShowAppointmentEditorModal(false)}
                onSave={handleUpdateAppointment}
                onDelete={handleDeleteAppointment}
                event={selectedAppointment}
                appointmentTypes={appointmentConfig?.appointmentMode === AppointmentMode.Types ? appointmentConfig?.appointmentTypes ?? [] : null}
            />

        </div>
    );
};

export default Calendar;
